/**
 * `<page-datasource-editor>` — the datasource connection wizard and the editor
 * for an existing catalog, routed at `/instances/:id/datasources/new` and
 * `/instances/:id/datasources/:name`.
 *
 * Implements `SC-F400` (the catalog name is the DuckDB catalog name and the form
 * says so), `SC-F401`–`SC-F404` (the six driver bodies, **generated from the form
 * schema** rather than from six hand-written field lists), `SC-F405` (the
 * credential is entered separately from the DSN, is write-only, and after save is
 * shown as *existing* through `bn-kv`'s redacted state and never as a value),
 * `SC-F407` (the verification step is **Validate configuration**, never "Test
 * connection", the copy explains why, the two badged-not-enforced field families
 * are rendered disabled with the schema's own reason, and the completion screen
 * links to the health surface rather than claiming success), `SC-F409` /
 * `SC-F607` (every datasource change is restart-class, stated on this screen
 * because this is where the change is made), `SC-F415` (the `path` picker offers
 * the files actually on the volume) and `SC-F416` (the sample catalog is an
 * ordinary datasource, so it opens in this editor like any other).
 *
 * Stories: `SC-S400`, `SC-S401`, `SC-S402`, `SC-S403`, `SC-S404`, `SC-S409`,
 * `SC-S410`.
 *
 * **The form is generated from the schema, not from this file** (frozen interface
 * `I3`). Per-type requiredness, the closed `DataSourceType` and `AttachMode`
 * vocabularies, the help text and the not-enforced statuses are all read from
 * `GET /v1/policy-schema`, which `internal/datasources` validates against too — so
 * the requiredness the browser enforces and the requiredness the route enforces
 * are one declaration rather than two that agree today. Nothing here hard-codes an
 * enum that the schema carries.
 *
 * **Step order, and why the check is last.** `03` §3.9 lists the wizard as name →
 * driver → connection → credentials → validate configuration → review. The check
 * runs `sluice datasource check` over the instance's **draft bundle**, so it can
 * only say anything true about this datasource once the record is in the draft:
 * running it before the save would validate a bundle that does not contain the
 * thing being validated. The save therefore comes before the check, and the
 * completion screen carries both the check's own prose and the health link.
 */

import { LitElement, css, html, nothing, type PropertyValues, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  toast,
  type BnKvRow,
  type BnSelectChangeDetail,
  type BnSelectOption,
} from "../components/index.js";

import { ApiError, parseError, validationFields } from "../api/errors.js";
import {
  checkDatasources,
  createDatasource,
  fieldBadge,
  fieldOffer,
  fieldStatusHelp,
  getDataSourceForm,
  getDatasource,
  listFiles,
  matchesDiscriminator,
  replaceDatasource,
  DATASOURCE_TYPES,
  type CheckReport,
  type DataSource,
  type DataSourceForm,
  type DataSourceType,
  type FormField,
  type PlacedFile,
  type ResolvedFormField,
  type WriteDataSourceInput,
} from "../api/datasources.js";
import { hrefFor, navigate } from "../nav.js";

/** The wizard's steps, in order. The edit form is not a wizard: an existing
 * catalog is one screen, because the reader is changing one field and not
 * discovering the shape. */
const STEPS = [
  { id: "name", label: "Catalog name" },
  { id: "driver", label: "Driver" },
  { id: "connection", label: "Connection" },
  { id: "credentials", label: "Credentials" },
  { id: "review", label: "Review and save" },
  { id: "verify", label: "Validate configuration" },
] as const;

type StepId = (typeof STEPS)[number]["id"];

/**
 * The manifest paths this form can set, mapped to the wire field of
 * `POST/PUT …/datasources`. It mirrors `datasources.Form.formFields` — the same
 * pairing the route walks the schema with — so a schema field the form cannot
 * express is detectable rather than silently dropped.
 *
 * `spec.credentialsRef` and `spec.tokenRef` are deliberately absent: both are
 * **derived** from the credential step, because a customer-authored
 * `secret://file//…` value would be an arbitrary-file-read primitive pointed at
 * `/run/secrets`.
 */
const WIRE_FIELD: Record<string, keyof WriteDataSourceInput> = {
  "spec.type": "type",
  "spec.connection": "connection",
  "spec.path": "path",
  "spec.bucket": "bucket",
  "spec.prefix": "prefix",
  "spec.region": "region",
  "spec.endpoint": "endpoint",
  "spec.database": "database",
  "spec.readonly": "readonly",
  "spec.attachMode": "attach_mode",
  "spec.allowedPaths": "allowed_paths",
};

/** The paths the control plane computes rather than accepts, with the reason. */
const DERIVED_PATHS: Record<string, string> = {
  "spec.credentialsRef":
    "Derived from the credential you enter below. A customer-authored secret reference is refused, because secret://file reads any absolute path the container can see.",
  "spec.tokenRef":
    "Derived from the credential you enter below — MotherDuck's spelling of the same material.",
};

@customElement("page-datasource-editor")
export class PageDatasourceEditor extends LitElement {
  /** The instance record id, from the route. */
  @property() instanceId = "";
  /** True on `…/datasources/new`. */
  @property({ type: Boolean }) create = false;
  /** The catalog name, on the edit route. */
  @property() name = "";

  /** The form schema (`I3`) — the contract this screen is generated from. */
  @state() private form: DataSourceForm | null = null;
  /** The record being edited, on the edit route. */
  @state() private existing: DataSource | null = null;
  /** Files on the state volume, for the `path` picker (`SC-F415`). */
  @state() private files: PlacedFile[] = [];
  @state() private fileRoot = "";

  @state() private loading = true;
  @state() private failure: ApiError | null = null;

  @state() private step: StepId = "name";

  /* ── the form's own values ─────────────────────────────────────────── */
  @state() private vName = "";
  @state() private vDisplayName = "";
  @state() private vType: DataSourceType | "" = "";
  @state() private vConnection = "";
  @state() private vPath = "";
  @state() private vBucket = "";
  @state() private vPrefix = "";
  @state() private vRegion = "";
  @state() private vEndpoint = "";
  @state() private vDatabase = "";
  @state() private vAllowedPaths = "";
  @state() private vReadonly = true;
  @state() private vAttachMode = "readonly";
  /** Write-only. Never populated from a read: there is nothing to populate it
   * from (`SC-S409`). */
  @state() private vSecret = "";
  @state() private vKeyId = "";
  @state() private vSessionToken = "";

  @state() private saving = false;
  @state() private saveFailure: ApiError | null = null;
  @state() private fieldErrors: Record<string, string> = {};
  /** `validation_failed` paths this form has no control for. Shown verbatim
   * rather than dropped: a rejected field the user cannot see is a dead end. */
  @state() private otherErrors: string[] = [];

  /** The saved record, which is what the verification step and the completion
   * screen are about. */
  @state() private saved: DataSource | null = null;

  @state() private check: CheckReport | null = null;
  @state() private checking = false;
  @state() private checkFailure: ApiError | null = null;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .wrap {
      max-width: 52rem;
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    h2 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 var(--bino-space-lg);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 68ch;
    }
    p.copy {
      margin: 0 0 var(--bino-space-sm);
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    p.copy:last-child {
      margin-bottom: 0;
    }
    p.copy strong {
      color: var(--bino-text);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .meta {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    /* The step rail. It is an ordered list because it is one: a reader using a
       screen reader hears "step 3 of 6" from the list semantics rather than from
       a colour. */
    ol.steps {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      margin: 0 0 var(--bino-space-lg);
      padding: 0;
      list-style: none;
      font-size: var(--bino-font-size-sm);
    }
    ol.steps li {
      display: flex;
      align-items: center;
      gap: var(--bino-space-xs);
      padding: var(--bino-space-xs) var(--bino-space-sm);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius-pill);
      color: var(--bino-text-secondary);
      background: var(--bino-surface);
    }
    ol.steps li[aria-current="step"] {
      border-color: var(--bino-primary);
      color: var(--bino-text);
      font-weight: 600;
    }
    ol.steps li.done {
      color: var(--bino-text-muted);
    }
    ol.steps .n {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
    }
    .section + .section {
      margin-top: var(--bino-space-lg);
    }
    .actions {
      display: flex;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      margin-top: var(--bino-space-lg);
    }
    .actions .spacer {
      flex: 1 1 auto;
    }
    .field-badge {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      margin-bottom: 0.25rem;
    }
    .badged-note {
      margin: 0.25rem 0 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 64ch;
    }
    .mono {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      overflow-wrap: anywhere;
    }
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
    }
    .failure p {
      margin: 0;
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    ul.plain {
      margin: 0;
      padding-left: 1.1rem;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    ul.plain li + li {
      margin-top: 0.25rem;
    }
    .driver-error {
      margin: var(--bino-space-xs) 0 0;
      padding: var(--bino-space-xs) var(--bino-space-sm);
      border: 1px solid var(--bino-error-border);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-error-bg);
      color: var(--bino-text);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      white-space: pre-wrap;
      overflow-x: auto;
    }
    .gaps {
      margin-top: var(--bino-space-md);
    }
  `;

  /** Driven from `willUpdate` alone: the router sets every property before the first
   * update, so loading from `connectedCallback` too would fetch the schema and the
   * record twice on every navigation. */
  override willUpdate(changed: PropertyValues<this>) {
    if (!this.instanceId) return;
    if (changed.has("instanceId") || changed.has("name") || changed.has("create")) {
      void this.load();
    }
  }

  private async load() {
    this.loading = true;
    this.failure = null;
    try {
      // The schema is the contract; without it there is no honest form to render,
      // so a schema failure is a page failure rather than a degraded form that
      // silently offers whatever this file happens to know.
      const [form, files] = await Promise.all([
        getDataSourceForm(),
        listFiles(this.instanceId).catch(() => null),
      ]);
      this.form = form;
      this.files = files?.items ?? [];
      this.fileRoot = files?.root ?? "";
      if (!this.create && this.name) {
        const existing = await getDatasource(this.instanceId, this.name);
        this.existing = existing;
        this.hydrate(existing);
      } else {
        this.applyInitialValues(form);
      }
    } catch (err) {
      this.failure = parseError(err);
    } finally {
      this.loading = false;
    }
  }

  /** applyInitialValues reads the schema's `initial` — the value the editor
   * pre-selects, which is a product decision and differs from Sluice's `default`
   * wherever our opinion differs from its silence. `readonly` is the case that
   * matters: the wire default is the permissive one. */
  private applyInitialValues(form: DataSourceForm) {
    for (const rf of form.fields) {
      const initial = rf.field.initial ?? rf.field.default;
      if (initial === undefined) continue;
      if (rf.path === "spec.readonly" && typeof initial === "boolean") this.vReadonly = initial;
      if (rf.path === "spec.attachMode" && typeof initial === "string") this.vAttachMode = initial;
    }
  }

  private hydrate(ds: DataSource) {
    this.vName = ds.name;
    this.vDisplayName = ds.display_name ?? "";
    this.vType = (ds.type as DataSourceType) || "";
    this.vConnection = ds.connection ?? "";
    this.vPath = ds.path ?? "";
    this.vBucket = ds.bucket ?? "";
    this.vPrefix = ds.prefix ?? "";
    this.vRegion = ds.region ?? "";
    this.vEndpoint = ds.endpoint ?? "";
    this.vDatabase = ds.database ?? "";
    this.vAllowedPaths = (ds.allowed_paths ?? []).join("\n");
    this.vReadonly = ds.readonly;
    this.vAttachMode = ds.attach_mode ?? "readonly";
    // Credentials are deliberately NOT hydrated: there is nothing to hydrate them
    // from, and an empty field with the "leave blank to keep" hint is the whole of
    // `SC-S409`'s second criterion.
    this.vSecret = "";
    this.vKeyId = "";
    this.vSessionToken = "";
  }

  /* ── the schema, read ─────────────────────────────────────────────── */

  /** specFields are the fields under `spec.`, in the schema's document order —
   * the order the form renders and the order the YAML view emits. */
  private get specFields(): ResolvedFormField[] {
    return (this.form?.fields ?? []).filter(
      (rf) => rf.path.startsWith("spec.") && rf.path !== "spec",
    );
  }

  /** enumOptions builds a picker from a closed vocabulary. A value the pinned
   * build refuses is **offered for recognition and not for selection**: it is
   * rendered disabled with the schema's own reason, because a value that silently
   * does nothing is worse than one that is visibly unavailable. */
  private enumOptions(enumName: string): BnSelectOption[] {
    const vocabulary = this.form?.enums[enumName];
    if (!vocabulary) return [];
    return vocabulary.values.map((v) => {
      const offer = v.status
        ? (this.form?.statuses.find((s) => s.status === v.status)?.offer ?? "no")
        : "yes";
      const disabled = offer === "no" || offer === "hidden";
      return {
        value: v.value,
        label: v.label || v.value,
        description: v.help,
        disabled,
        disabledReason: disabled
          ? [v.reason, v.upstream ? `Tracked as ${v.upstream}.` : ""].filter(Boolean).join(" ") ||
            "This build does not act on this value."
          : undefined,
      };
    });
  }

  /** applies decides whether a field belongs to the chosen driver. Both
   * conditions are read from the schema: `requiredWhen` doubles as `appliesWhen`
   * for the four discriminated scalars, because the schema does not repeat
   * itself. */
  private applies(field: FormField): boolean {
    if (!this.vType) return false;
    if (field.appliesWhen) return matchesDiscriminator(field.appliesWhen, this.vType);
    if (field.requiredWhen) return matchesDiscriminator(field.requiredWhen, this.vType);
    return true;
  }

  private required(field: FormField): boolean {
    if (field.required) return true;
    return !!field.requiredWhen && matchesDiscriminator(field.requiredWhen, this.vType);
  }

  /* ── save ─────────────────────────────────────────────────────────── */

  private body(): WriteDataSourceInput {
    const allowed = this.vAllowedPaths
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line !== "");
    const input: WriteDataSourceInput = {
      name: this.vName.trim(),
      type: (this.vType || "postgres") as DataSourceType,
      // Sent explicitly, always: the wire default is the permissive one, so
      // omitting it would make "the client did not say" and "attach the catalog
      // writable" the same request.
      readonly: this.vReadonly,
    };
    if (this.vDisplayName.trim()) input.display_name = this.vDisplayName.trim();
    if (this.vAttachMode) input.attach_mode = this.vAttachMode;
    if (this.applies(this.fieldAt("spec.connection")) && this.vConnection.trim())
      input.connection = this.vConnection.trim();
    if (this.applies(this.fieldAt("spec.path")) && this.vPath.trim()) input.path = this.vPath.trim();
    if (this.applies(this.fieldAt("spec.bucket")) && this.vBucket.trim())
      input.bucket = this.vBucket.trim();
    if (this.applies(this.fieldAt("spec.prefix")) && this.vPrefix.trim())
      input.prefix = this.vPrefix.trim();
    if (this.applies(this.fieldAt("spec.region")) && this.vRegion.trim())
      input.region = this.vRegion.trim();
    if (this.applies(this.fieldAt("spec.endpoint")) && this.vEndpoint.trim())
      input.endpoint = this.vEndpoint.trim();
    if (this.applies(this.fieldAt("spec.database")) && this.vDatabase.trim())
      input.database = this.vDatabase.trim();
    if (this.applies(this.fieldAt("spec.allowedPaths")) && allowed.length > 0)
      input.allowed_paths = allowed;
    const credentials: WriteDataSourceInput["credentials"] = {};
    if (this.vSecret) credentials.secret = this.vSecret;
    if (this.vKeyId) credentials.key_id = this.vKeyId;
    if (this.vSessionToken) credentials.session_token = this.vSessionToken;
    if (Object.keys(credentials).length > 0) input.credentials = credentials;
    return input;
  }

  /** fieldAt looks one schema field up by path, answering an inert field when the
   * schema does not carry it — so a missing path degrades to "not applicable"
   * rather than throwing inside a render. */
  private fieldAt(path: string): FormField {
    const found = this.specFields.find((rf) => rf.path === path);
    return found?.field ?? { name: path, type: "string" };
  }

  private async save() {
    this.saving = true;
    this.saveFailure = null;
    this.fieldErrors = {};
    this.otherErrors = [];
    try {
      const body = this.body();
      const saved = this.create
        ? await createDatasource(this.instanceId, body)
        : await replaceDatasource(this.instanceId, this.name, body);
      this.saved = saved;
      this.existing = saved;
      // The credential fields are cleared the moment the save lands, so a
      // plaintext password does not sit in a DOM property after it has been
      // sealed.
      this.vSecret = "";
      this.vKeyId = "";
      this.vSessionToken = "";
      if (this.create) {
        this.step = "verify";
        void this.runCheck();
      } else {
        toast(
          `${saved.name} is saved to the draft. Publishing it restarts the instance.`,
          "success",
          8000,
        );
      }
    } catch (err) {
      const failure = parseError(err);
      this.saveFailure = failure;
      const fields: Record<string, string> = {};
      const others: string[] = [];
      for (const f of validationFields(failure)) {
        if (KNOWN_WIRE_FIELDS.has(f.path) || f.path.startsWith("allowed_paths")) {
          fields[f.path] = f.reason;
        } else {
          others.push(`${f.path}: ${f.reason}`);
        }
      }
      this.fieldErrors = fields;
      this.otherErrors = others;
      // A field-level refusal belongs on the step that owns the field, so the
      // reader is not left on a review screen with a message about a control two
      // steps back.
      if (this.create) this.step = this.stepForErrors(fields);
    } finally {
      this.saving = false;
    }
  }

  private stepForErrors(fields: Record<string, string>): StepId {
    const paths = Object.keys(fields);
    if (paths.some((p) => p === "name" || p === "display_name")) return "name";
    if (paths.some((p) => p === "type")) return "driver";
    if (paths.some((p) => p === "credentials")) return "credentials";
    if (paths.length > 0) return "connection";
    return "review";
  }

  private async runCheck() {
    this.checking = true;
    this.checkFailure = null;
    try {
      this.check = await checkDatasources(this.instanceId);
    } catch (err) {
      this.check = null;
      this.checkFailure = parseError(err);
    } finally {
      this.checking = false;
    }
  }

  /* ── rendering: shared ────────────────────────────────────────────── */

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  private renderFailure(heading: string, err: ApiError, retry: () => void) {
    return html`<bn-card heading=${heading}>
      <div class="failure" role="alert">
        <p>${err.message}</p>
        <bn-kv .rows=${this.supportRows(err)}></bn-kv>
        ${err.requestId
          ? nothing
          : html`<p>This request did not reach Sluice Cloud, so there is no reference to quote.</p>`}
        <div class="actions"><bn-button variant="primary" @click=${retry}>Try again</bn-button></div>
      </div>
    </bn-card>`;
  }

  /** The restart-class statement. It is on this screen because `SC-F409` requires
   * it where the change is made, not only in the publish dialog. */
  private renderRestartNotice() {
    return html`<bn-panel eyebrow="Saving here changes the draft; publishing restarts the instance">
      <p class="copy">
        A catalog is attached only while the Sluice process starts, and
        <code>datasources.reload: true</code> is rejected by <code>sluice config validate</code> and
        again at boot. So every datasource change is <strong>restart-class</strong>: publishing it
        replaces the running task, queries fail for roughly 10 to 25 seconds, and DuckDB re-attaches
        and re-warms afterwards. A policy publish costs no restart at all.
      </p>
    </bn-panel>`;
  }

  /**
   * renderBadgedField renders a field the pinned build does not act on: visible,
   * disabled, with the schema's badge, its reason and its upstream citation
   * (`SC-F407`, `SC-F505`, `concept.md` principle 8).
   *
   * It is never silently dropped. A field that is not offered teaches the reader
   * that the control they were looking for does nothing; a field that is missing
   * teaches them nothing at all, and they go looking for it in the YAML.
   */
  private renderBadgedField(rf: ResolvedFormField) {
    const form = this.form;
    if (!form) return nothing;
    const badge = fieldBadge(form, rf.field);
    const help = fieldStatusHelp(form, rf.field);
    return html`<div class="section">
      <div class="field-badge">
        <span class="mono">${rf.path}</span>
        <bn-badge variant="not-enforced">${badge}</bn-badge>
      </div>
      ${rf.field.type === "object"
        ? // A whole inert block — `spec.healthCheck` is the one on this kind. Its
          // children are rendered as their own badged rows below it, so drawing a
          // disabled control for the container would imply a control that never
          // existed.
          html`<p class="copy"><strong>${labelFor(rf)}</strong></p>`
        : html`<bn-field label=${labelFor(rf)}>
            <bn-input disabled placeholder="Not available in this build"></bn-input>
          </bn-field>`}
      <p class="badged-note">
        ${rf.field.reason ? html`${capitalize(rf.field.reason)}. ` : nothing}${rf.field.help ?? ""}
        ${help ? html` ${help}` : nothing}
        ${rf.field.source ? html` <span class="mono">(${rf.field.source})</span>` : nothing}
        ${rf.field.upstream ? html` Tracked upstream as ${rf.field.upstream}.` : nothing}
      </p>
    </div>`;
  }

  /** renderDerivedField shows a manifest field the control plane computes, so the
   * reader can see it exists without being offered a value that would be a
   * file-read primitive. */
  private renderDerivedField(rf: ResolvedFormField, reason: string) {
    const ref = this.existing?.credentials_ref;
    return html`<div class="section">
      <div class="field-badge">
        <span class="mono">${rf.path}</span>
        <bn-badge variant="neutral">computed</bn-badge>
      </div>
      ${ref
        ? html`<bn-kv .rows=${[{ label: "Rendered reference", value: ref }] as BnKvRow[]}></bn-kv>`
        : html`<p class="meta">Written when the credential below is sealed.</p>`}
      <p class="badged-note">${reason}</p>
    </div>`;
  }

  /* ── rendering: the driver-specific body, from the schema ─────────── */

  private renderSpecField(rf: ResolvedFormField): TemplateResult | typeof nothing {
    const form = this.form;
    if (!form) return nothing;
    const offer = fieldOffer(form, rf.field);
    if (offer === "hidden" || offer === "no" || offer === "badged") {
      // `hidden` is not a milder `no` in the schema's vocabulary, but for a field
      // this build does not act on the product's answer is the same on this
      // screen: show it, disabled, with the reason (`SC-F407` allows hidden or
      // badged; badged is what teaches).
      return this.renderBadgedField(rf);
    }
    // Applicability is checked before the derived branch, so a file-backed
    // catalog does not advertise a credential reference it will never carry.
    if (!this.applies(rf.field)) return nothing;
    const derived = DERIVED_PATHS[rf.path];
    if (derived) return this.renderDerivedField(rf, derived);
    const wire = WIRE_FIELD[rf.path];
    if (!wire) return nothing;

    switch (rf.path) {
      case "spec.type":
        return this.renderTypeField(rf);
      case "spec.readonly":
        return this.renderReadonlyField(rf);
      case "spec.attachMode":
        return this.renderAttachModeField(rf);
      case "spec.path":
        return this.renderPathField(rf);
      case "spec.allowedPaths":
        return this.renderAllowedPathsField(rf);
      case "spec.connection":
        return this.renderConnectionField(rf);
      default:
        return this.renderTextField(rf, String(wire));
    }
  }

  private renderTypeField(rf: ResolvedFormField) {
    const options = this.enumOptions(rf.field.enum ?? "DataSourceType");
    const fallback: BnSelectOption[] = DATASOURCE_TYPES.map((t) => ({ value: t, label: t }));
    return html`<bn-field
      label="Driver"
      required
      .error=${this.fieldErrors["type"] ?? ""}
      hint=${rf.field.help ??
      "Fixed after creation: switching driver is a delete plus a create, because the required fields differ entirely."}
    >
      <bn-select
        .options=${options.length > 0 ? options : fallback}
        .value=${this.vType}
        ?disabled=${!this.create}
        ?invalid=${!!this.fieldErrors["type"]}
        @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
          (this.vType = e.detail.value as DataSourceType)}
      ></bn-select>
    </bn-field>`;
  }

  private renderReadonlyField(rf: ResolvedFormField) {
    return html`<bn-checkbox
      label="Attach the catalog read-only"
      hint=${rf.field.help ??
      "Clearing this is a widening change: the wire default is the permissive one, so the form always states this field explicitly."}
      .checked=${this.vReadonly}
      @change=${(e: CustomEvent<{ checked: boolean }>) => (this.vReadonly = e.detail.checked)}
    ></bn-checkbox>`;
  }

  private renderAttachModeField(rf: ResolvedFormField) {
    const options = this.enumOptions(rf.field.enum ?? "AttachMode");
    return html`<bn-field
      label="Attach mode"
      .error=${this.fieldErrors["attach_mode"] ?? ""}
      hint="Sluice accepts read/write and does not validate it away; this product refuses it, because a writable attach on a governance gateway is a data-loss surface."
    >
      <bn-select
        .options=${options}
        .value=${this.vAttachMode}
        ?invalid=${!!this.fieldErrors["attach_mode"]}
        @change=${(e: CustomEvent<BnSelectChangeDetail>) => (this.vAttachMode = e.detail.value)}
      ></bn-select>
    </bn-field>`;
  }

  /** The path picker (`SC-F402`, `SC-F415`). It offers the files that are actually
   * on the volume, because a path the customer typed and no file matches produces
   * a datasource that publishes, restarts the instance and then fails to attach.
   * The picker is a convenience: the same allowlist is enforced by the route and
   * again by the publish gate's rule L32. */
  private renderPathField(rf: ResolvedFormField) {
    const options: BnSelectOption[] = this.files.map((f) => ({
      value: f.path,
      label: f.name,
      description: `${f.path}${f.used_by && f.used_by.length > 0 ? ` · used by ${f.used_by.join(", ")}` : ""}`,
    }));
    return html`<div class="section">
      ${options.length > 0
        ? html`<bn-field
            label="File on the state volume"
            required
            .error=${this.fieldErrors["path"] ?? ""}
            hint=${rf.field.help ?? ""}
          >
            <bn-select
              .options=${options}
              .value=${this.vPath}
              ?invalid=${!!this.fieldErrors["path"]}
              @change=${(e: CustomEvent<BnSelectChangeDetail>) => (this.vPath = e.detail.value)}
            ></bn-select>
          </bn-field>`
        : html`<bn-field
              label="File path"
              required
              .error=${this.fieldErrors["path"] ?? ""}
              hint="Must be a plain file directly under ${this.fileRoot || "/var/lib/sluice/files/"}."
            >
              <bn-input
                mono
                .value=${this.vPath}
                ?invalid=${!!this.fieldErrors["path"]}
                @input=${(e: CustomEvent<{ value: string }>) => (this.vPath = e.detail.value)}
              ></bn-input>
            </bn-field>
            <p class="copy">
              No files are placed on this instance's volume yet. Sluice Cloud does not upload the file
              for you as part of this form — placing one is a separate action on the datasources
              screen, and until the file exists the catalog will be reported unhealthy with the
              driver's own error.
            </p>`}
    </div>`;
  }

  private renderAllowedPathsField(rf: ResolvedFormField) {
    return html`<bn-field
      label="Allowed paths"
      .error=${this.fieldErrors["allowed_paths"] ?? firstIndexedError(this.fieldErrors, "allowed_paths")}
      hint=${rf.field.help ??
      "One wildcard pattern per line. Adding entries widens what may be read from the bucket."}
    >
      <bn-code
        language="plain"
        hide-toolbar
        .readonly=${false}
        .value=${this.vAllowedPaths}
        label="Allowed path patterns, one per line"
        @bn-code-change=${(e: CustomEvent<{ value: string }>) =>
          (this.vAllowedPaths = e.detail.value)}
      ></bn-code>
    </bn-field>`;
  }

  /**
   * The DSN field (`SC-F401`). The hint carries the one rule a customer gets
   * wrong: the password does **not** go here. The route refuses a DSN carrying
   * one — Sluice reads the password only from the sealed credential, and a DSN
   * password would be stored in a rendered policy file that is not encrypted at
   * rest and returned by every read.
   */
  private renderConnectionField(rf: ResolvedFormField) {
    return html`<bn-field
      label="Connection string"
      required
      .error=${this.fieldErrors["connection"] ?? ""}
      hint="postgres://user@host:5432/database — no password. Enter the password in the credential field, which only ever writes."
    >
      <bn-input
        mono
        .value=${this.vConnection}
        ?invalid=${!!this.fieldErrors["connection"]}
        placeholder="postgres://analytics@warehouse.internal:5432/shop"
        @input=${(e: CustomEvent<{ value: string }>) => (this.vConnection = e.detail.value)}
      ></bn-input>
    </bn-field>
    ${rf.field.help ? html`<p class="badged-note">${rf.field.help}</p>` : nothing}`;
  }

  private renderTextField(rf: ResolvedFormField, wire: string) {
    const value = this.textValue(wire);
    return html`<bn-field
      label=${labelFor(rf)}
      ?required=${this.required(rf.field)}
      .error=${this.fieldErrors[wire] ?? ""}
      hint=${rf.field.help ?? ""}
    >
      <bn-input
        ?mono=${rf.field.format === "url" || rf.field.format === "file-path"}
        .value=${value}
        ?invalid=${!!this.fieldErrors[wire]}
        @input=${(e: CustomEvent<{ value: string }>) => this.setText(wire, e.detail.value)}
      ></bn-input>
    </bn-field>`;
  }

  private textValue(wire: string): string {
    switch (wire) {
      case "bucket":
        return this.vBucket;
      case "prefix":
        return this.vPrefix;
      case "region":
        return this.vRegion;
      case "endpoint":
        return this.vEndpoint;
      case "database":
        return this.vDatabase;
      default:
        return "";
    }
  }

  private setText(wire: string, value: string) {
    switch (wire) {
      case "bucket":
        this.vBucket = value;
        break;
      case "prefix":
        this.vPrefix = value;
        break;
      case "region":
        this.vRegion = value;
        break;
      case "endpoint":
        this.vEndpoint = value;
        break;
      case "database":
        this.vDatabase = value;
        break;
    }
  }

  /* ── rendering: the credential step (`SC-F405`) ───────────────────── */

  private get takesCredential(): boolean {
    return this.vType !== "sqlite" && this.vType !== "duckdb_file" && this.vType !== "";
  }

  private renderCredentialStep() {
    if (!this.takesCredential) {
      return html`<bn-card heading="Credentials">
        <p class="copy">
          A <code>${this.vType}</code> catalog reads a file on this instance's own volume, so there is
          nothing to authenticate to. No credential is stored, and passing one is refused rather than
          ignored — an ignored credential is a sealed secret with nothing reading it.
        </p>
      </bn-card>`;
    }
    const has = this.existing?.has_credential ?? false;
    const isS3 = this.vType === "s3";
    return html`<bn-card heading="Credentials">
      <p class="copy">
        The credential is entered <strong>here</strong> and never in the connection string. It is
        sealed in control-plane storage, materialized as a Kubernetes Secret mounted as a file
        inside the container, and the rendered manifest carries
        <code>credentialsRef: secret://file//run/secrets/&lt;name&gt;</code> — never the value. After
        save it never round-trips to the browser again.
      </p>
      ${has
        ? html`<bn-kv
              .rows=${[{ label: "Stored credential", redacted: true }] as BnKvRow[]}
              redactedText="set · never shown"
              redactedHint="Sealed at rest and materialized as a file inside the container. There is nothing here to copy."
            ></bn-kv>
            <p class="meta">
              Leave the fields below blank to keep the current value. Replacing it mints a new secret
              object, which is a new content address — and that is a restart.
            </p>`
        : nothing}
      ${isS3
        ? html`<div class="section">
              <bn-field label="Access key id" ?required=${!has}>
                <bn-input
                  mono
                  autocomplete="off"
                  .value=${this.vKeyId}
                  @input=${(e: CustomEvent<{ value: string }>) => (this.vKeyId = e.detail.value)}
                ></bn-input>
              </bn-field>
            </div>
            <div class="section">
              <bn-field label="Secret access key" ?required=${!has}>
                <bn-input
                  type="password"
                  autocomplete="new-password"
                  .value=${this.vSecret}
                  @input=${(e: CustomEvent<{ value: string }>) => (this.vSecret = e.detail.value)}
                ></bn-input>
              </bn-field>
            </div>
            <div class="section">
              <bn-field label="Session token" hint="Optional, for temporary credentials.">
                <bn-input
                  type="password"
                  autocomplete="off"
                  .value=${this.vSessionToken}
                  @input=${(e: CustomEvent<{ value: string }>) =>
                    (this.vSessionToken = e.detail.value)}
                ></bn-input>
              </bn-field>
            </div>`
        : html`<div class="section">
            <bn-field
              label=${this.vType === "motherduck" ? "Service token" : "Password"}
              ?required=${!has}
              .error=${this.fieldErrors["credentials"] ?? ""}
              hint=${has ? "Leave blank to keep the current value." : ""}
            >
              <bn-input
                type="password"
                autocomplete="new-password"
                .value=${this.vSecret}
                ?invalid=${!!this.fieldErrors["credentials"]}
                @input=${(e: CustomEvent<{ value: string }>) => (this.vSecret = e.detail.value)}
              ></bn-input>
            </bn-field>
          </div>`}
      <p class="meta" style="margin-top: var(--bino-space-md)">
        Only <code>env</code> and <code>file</code> secret providers exist in this build;
        <code>vault</code>, <code>aws-sm</code> and <code>gcp-sm</code> are rejected at parse and would
        fail both <code>config validate</code> and boot, so the product does not offer them.
      </p>
    </bn-card>`;
  }

  /* ── rendering: steps ─────────────────────────────────────────────── */

  private renderNameStep() {
    return html`<bn-card heading="Catalog name">
      <p class="copy">
        This name <strong>is</strong> the DuckDB catalog name. Every policy's
        <code>resources.catalogs</code> pattern matches against it, so renaming it later silently
        invalidates every policy that referenced the old one — the policies still compile, they simply
        stop matching. Choose it as deliberately as you would a table name.
      </p>
      <bn-field
        label="Catalog name"
        required
        .error=${this.fieldErrors["name"] ?? ""}
        hint="Lower-case letters, digits and hyphens, starting and ending alphanumeric. At most 63 characters."
      >
        <bn-input
          mono
          .value=${this.vName}
          ?disabled=${!this.create}
          ?invalid=${!!this.fieldErrors["name"]}
          placeholder="warehouse"
          @input=${(e: CustomEvent<{ value: string }>) => (this.vName = e.detail.value)}
        ></bn-input>
      </bn-field>
      <div class="section">
        <bn-field
          label="Display name"
          .error=${this.fieldErrors["display_name"] ?? ""}
          hint="Optional, and the only field on this form whose change is not a restart: it reaches no manifest."
        >
          <bn-input
            .value=${this.vDisplayName}
            ?invalid=${!!this.fieldErrors["display_name"]}
            @input=${(e: CustomEvent<{ value: string }>) => (this.vDisplayName = e.detail.value)}
          ></bn-input>
        </bn-field>
      </div>
    </bn-card>`;
  }

  private renderDriverStep() {
    const typeField = this.specFields.find((rf) => rf.path === "spec.type");
    return html`<bn-card heading="Driver">
      ${typeField ? this.renderTypeField(typeField) : nothing}
      <p class="copy" style="margin-top: var(--bino-space-md)">
        ${this.form?.enums["DataSourceType"]?.help ?? ""}
      </p>
    </bn-card>`;
  }

  private renderConnectionStep() {
    const fields = this.specFields.filter(
      (rf) => rf.path !== "spec.type" && rf.path !== "spec.readonly" && rf.path !== "spec.attachMode",
    );
    const readonlyField = this.specFields.find((rf) => rf.path === "spec.readonly");
    const attachField = this.specFields.find((rf) => rf.path === "spec.attachMode");
    return html`<bn-card heading="Connection">
      <p class="copy">
        Only the fields the chosen driver reads are offered. A field belonging to another driver is
        not merely useless — the route refuses it, because a driver ignoring a value the customer
        filled in is indistinguishable from the value working.
      </p>
      ${fields.map((rf) => html`<div class="section">${this.renderSpecField(rf)}</div>`)}
      <div class="section">${readonlyField ? this.renderSpecField(readonlyField) : nothing}</div>
      <div class="section">${attachField ? this.renderSpecField(attachField) : nothing}</div>
    </bn-card>`;
  }

  /** The review step. It states the blast radius before the save, because on a
   * datasource the save is the commitment to a restart at the next publish. */
  private renderReviewStep() {
    const rows: BnKvRow[] = [
      { label: "Catalog name", value: this.vName },
      { label: "Driver", value: this.vType || "—" },
      ...(this.vConnection ? [{ label: "Connection", value: this.vConnection }] : []),
      ...(this.vPath ? [{ label: "File", value: this.vPath }] : []),
      ...(this.vBucket ? [{ label: "Bucket", value: this.vBucket }] : []),
      ...(this.vPrefix ? [{ label: "Prefix", value: this.vPrefix }] : []),
      ...(this.vRegion ? [{ label: "Region", value: this.vRegion }] : []),
      ...(this.vEndpoint ? [{ label: "Endpoint", value: this.vEndpoint }] : []),
      ...(this.vDatabase ? [{ label: "Database", value: this.vDatabase }] : []),
      { label: "Attach", value: this.vReadonly ? "read-only" : "read/write (widening)" },
      ...(this.takesCredential
        ? [
            this.vSecret || this.vKeyId
              ? { label: "Credential", redacted: true }
              : { label: "Credential", value: this.existing?.has_credential ? "unchanged" : "none" },
          ]
        : []),
    ];
    return html`<bn-card heading="Review">
      <bn-kv .rows=${rows}></bn-kv>
      ${this.otherErrors.length > 0
        ? html`<div role="alert" style="margin-top: var(--bino-space-md)">
            <p class="copy"><strong>The API refused fields this form does not show:</strong></p>
            <ul class="plain">
              ${this.otherErrors.map((e) => html`<li>${e}</li>`)}
            </ul>
          </div>`
        : nothing}
    </bn-card>`;
  }

  /**
   * The verification step (`SC-F407`, `SC-S403`). It is labelled **Validate
   * configuration**; the copy states what the command does and does not do; and
   * the completion links to the health surface — the place connectivity is
   * actually proven — rather than claiming success.
   */
  private renderVerifyStep() {
    const saved = this.saved;
    return html`<bn-card heading="Validate configuration">
      <p class="copy">
        <strong>This is not a connection test, and it is not called one.</strong>
        <code>sluice datasource check</code> loads the draft's manifests on your instance's own pinned
        image and reports whether a registered driver factory exists for each declared type, plus the
        filters each one declares. It constructs no driver, opens no socket and issues no probe.
      </p>
      <p class="copy">
        Live probing is <code>serve</code>'s health loop, which means connectivity is proven only once
        the instance runs the published configuration.
      </p>
      ${this.checking
        ? html`<bn-spinner label="Running the configuration check on your instance"></bn-spinner>`
        : nothing}
      ${this.checkFailure
        ? html`<div class="failure" role="alert" style="margin-top: var(--bino-space-md)">
            <p>${this.checkFailure.message}</p>
            <bn-kv .rows=${this.supportRows(this.checkFailure)}></bn-kv>
            <div class="actions">
              <bn-button @click=${() => void this.runCheck()}>Run it again</bn-button>
            </div>
          </div>`
        : nothing}
      ${this.check ? this.renderCheckReport(this.check) : nothing}
      ${saved
        ? html`<div class="section">
            <h2>What is proven, and what is not</h2>
            <ul class="plain">
              <li>
                <strong>${saved.name}</strong> is in the draft. Nothing on the instance has changed
                yet.
              </li>
              <li>
                Publishing it <strong>restarts</strong> the instance: roughly 10 to 25 seconds during
                which queries fail, then DuckDB re-attaches and re-warms the catalogs.
              </li>
              <li>
                Whether the credentials, the network path and the filters actually work is answered by
                the instance's own health loop after that restart — on the datasource health surface.
              </li>
            </ul>
            <div class="actions">
              <bn-button
                @click=${() => navigate(hrefFor("instances", this.instanceId, "datasources"))}
              >
                Datasource health
              </bn-button>
              <bn-button
                variant="primary"
                @click=${() => navigate(hrefFor("instances", this.instanceId, "publish"))}
              >
                Review and publish
              </bn-button>
            </div>
          </div>`
        : nothing}
    </bn-card>`;
  }

  private renderCheckReport(report: CheckReport) {
    return html`<div class="section">
      <div class="field-badge">
        <bn-badge .variant=${report.ok ? "success" : "error"}>
          ${report.ok
            ? "Every declared type has a driver in this build"
            : `${report.failed} declared type(s) have no driver`}
        </bn-badge>
        <span class="meta">${report.checked}</span>
      </div>
      <p class="copy"><strong>${report.notice}</strong></p>
      <h2>Not checked</h2>
      <ul class="plain">
        ${report.not_checked.map((n) => html`<li>${n}</li>`)}
      </ul>
      ${report.datasources
        .filter((row) => row.error)
        .map((row) => html`<p class="driver-error">${row.name}: ${row.error}</p>`)}
      ${report.datasources.some((row) => !row.factory_known)
        ? html`<p class="meta">
            The six driver types this control plane models: ${report.known_types.join(", ")}. Your
            instance's own registry is the authority, and its list is quoted in the error above.
          </p>`
        : nothing}
    </div>`;
  }

  private renderSteps() {
    const index = STEPS.findIndex((s) => s.id === this.step);
    return html`<ol class="steps" aria-label="Wizard steps">
      ${STEPS.map(
        (s, i) => html`<li
          class=${i < index ? "done" : ""}
          aria-current=${s.id === this.step ? "step" : nothing}
        >
          <span class="n">${i + 1}</span>
          <span>${s.label}</span>
        </li>`,
      )}
    </ol>`;
  }

  private stepBody() {
    switch (this.step) {
      case "name":
        return this.renderNameStep();
      case "driver":
        return this.renderDriverStep();
      case "connection":
        return this.renderConnectionStep();
      case "credentials":
        return this.renderCredentialStep();
      case "review":
        return this.renderReviewStep();
      case "verify":
        return this.renderVerifyStep();
    }
  }

  private canAdvance(): boolean {
    switch (this.step) {
      case "name":
        return this.vName.trim() !== "";
      case "driver":
        return this.vType !== "";
      default:
        return true;
    }
  }

  private goto(delta: number) {
    const index = STEPS.findIndex((s) => s.id === this.step);
    const next = STEPS[Math.min(STEPS.length - 1, Math.max(0, index + delta))];
    if (next) this.step = next.id;
  }

  private renderWizardActions() {
    if (this.step === "verify") return nothing;
    const first = this.step === "name";
    const review = this.step === "review";
    return html`<div class="actions">
      <bn-button
        ?disabled=${first}
        @click=${() => this.goto(-1)}
      >
        Back
      </bn-button>
      <span class="spacer"></span>
      <bn-button
        @click=${() => navigate(hrefFor("instances", this.instanceId, "datasources"))}
      >
        Cancel
      </bn-button>
      ${review
        ? html`<bn-button
            variant="primary"
            ?loading=${this.saving}
            @click=${() => void this.save()}
          >
            Save to the draft
          </bn-button>`
        : html`<bn-button
            variant="primary"
            ?disabled=${!this.canAdvance()}
            @click=${() => this.goto(1)}
          >
            Continue
          </bn-button>`}
    </div>`;
  }

  /** The schema's own statement of what it does not carry (`SC-F501`). It is
   * rendered here because this is the screen the schema generates, and a form
   * generator's reader is entitled to know the source's limits. */
  private renderProvenance() {
    const form = this.form;
    if (!form) return nothing;
    return html`<bn-panel eyebrow="Where this form comes from">
      <p class="copy">
        Every field, its requiredness per driver and every closed vocabulary on this screen are read
        from the form schema — version <code>${form.formSchemaVersion}</code>, checked against Sluice
        <code>${form.sluiceCommit.slice(0, 12)}</code>. ${form.source}
        ${form.upstream ? html`The upstream ask that would retire it is ${form.upstream}.` : nothing}
      </p>
      ${form.knownGaps.length > 0
        ? html`<div class="gaps">
            <h2>What the upstream schema export cannot say</h2>
            <ul class="plain">
              ${form.knownGaps.map((g) => html`<li>${g}</li>`)}
            </ul>
          </div>`
        : nothing}
    </bn-panel>`;
  }

  private renderEditForm() {
    const fields = this.specFields.filter((rf) => rf.path !== "spec.type");
    const typeField = this.specFields.find((rf) => rf.path === "spec.type");
    return html`<div class="section">
      ${this.renderNameStep()}
      <div class="section">
        <bn-card heading="Driver and connection">
          ${typeField ? this.renderTypeField(typeField) : nothing}
          ${fields.map((rf) => html`<div class="section">${this.renderSpecField(rf)}</div>`)}
        </bn-card>
      </div>
      <div class="section">${this.renderCredentialStep()}</div>
      ${this.saveFailure
        ? html`<div class="section">
            <bn-card heading="The save was refused">
              <div class="failure" role="alert">
                <p>${this.saveFailure.message}</p>
                <bn-kv .rows=${this.supportRows(this.saveFailure)}></bn-kv>
                ${this.otherErrors.length > 0
                  ? html`<ul class="plain">
                      ${this.otherErrors.map((e) => html`<li>${e}</li>`)}
                    </ul>`
                  : nothing}
              </div>
            </bn-card>
          </div>`
        : nothing}
      <div class="actions">
        <bn-button @click=${() => navigate(hrefFor("instances", this.instanceId, "datasources"))}>
          Cancel
        </bn-button>
        <span class="spacer"></span>
        <bn-button ?loading=${this.checking} @click=${() => void this.runCheck()}>
          Validate configuration
        </bn-button>
        <bn-button variant="primary" ?loading=${this.saving} @click=${() => void this.save()}>
          Save to the draft
        </bn-button>
      </div>
      ${this.check || this.checkFailure
        ? html`<div class="section">${this.renderVerifyStep()}</div>`
        : nothing}
    </div>`;
  }

  override render() {
    if (this.loading) {
      return html`<bn-spinner block size="28" label="Loading the datasource form"></bn-spinner>`;
    }
    if (this.failure) {
      return html`<a class="back" href=${hrefFor("instances", this.instanceId, "datasources")}
          >← Datasources</a
        >
        ${this.renderFailure(
          this.create ? "Could not open the datasource form" : "Could not load this datasource",
          this.failure,
          () => void this.load(),
        )}`;
    }
    const heading = this.create
      ? "Connect a datasource"
      : `${this.existing?.name ?? this.name}`;
    return html`<div class="wrap">
      <a class="back" href=${hrefFor("instances", this.instanceId, "datasources")}>← Datasources</a>
      <h1>${heading}</h1>
      <p class="lede">
        ${this.create
          ? html`Six drivers, one shared credential step and one honest verification step. The form is
            generated from the pinned build's own field schema, so what it offers is what that build
            reads.`
          : html`Changing a catalog edits the <strong>draft</strong>. The driver and the catalog name
            are fixed after creation: switching either is a delete plus a create, because a rename
            silently stops every policy that named the old catalog from matching.`}
      </p>
      ${this.existing?.sample
        ? html`<bn-panel eyebrow="Sample data">
            <p class="copy">
              This is the seeded sample catalog. It is an ordinary <code>DataSource</code> manifest —
              nothing about it is hidden or privileged — and every surface that shows its data badges
              it as sample data.
            </p>
          </bn-panel>`
        : nothing}
      ${this.renderRestartNotice()}
      ${this.create
        ? html`${this.renderSteps()} ${this.stepBody()} ${this.renderWizardActions()}`
        : this.renderEditForm()}
      ${this.saveFailure && this.create
        ? html`<div class="section" role="alert">
            <bn-card heading="The save was refused">
              <p class="copy">${this.saveFailure.message}</p>
              <bn-kv .rows=${this.supportRows(this.saveFailure)}></bn-kv>
            </bn-card>
          </div>`
        : nothing}
      <div class="section">${this.renderProvenance()}</div>
    </div>`;
  }
}

/** The wire field names this form owns, so a `validation_failed` path it cannot
 * place is shown verbatim rather than attached to the wrong control. */
const KNOWN_WIRE_FIELDS = new Set<string>([
  "name",
  "display_name",
  "type",
  "connection",
  "path",
  "bucket",
  "prefix",
  "region",
  "endpoint",
  "database",
  "readonly",
  "attach_mode",
  "allowed_paths",
  "credentials",
]);

/** firstIndexedError finds a refusal on an element of a list field —
 * `allowed_paths[2]` — so the message lands on the control that holds it. */
function firstIndexedError(errors: Record<string, string>, prefix: string): string {
  const key = Object.keys(errors).find((k) => k.startsWith(`${prefix}[`));
  return key ? `${key}: ${errors[key]}` : "";
}

/** labelFor turns a schema path into a form label. The schema carries help text
 * and status, not display labels, so the label is derived from the field name —
 * one place, rather than a second name registry to keep in step. */
function labelFor(rf: ResolvedFormField): string {
  const name = rf.path.split(".").pop() ?? rf.path;
  const spaced = name.replace(/\[\]$/, "").replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

declare global {
  interface HTMLElementTagNameMap {
    "page-datasource-editor": PageDatasourceEditor;
  }
}
